import React, { useState, useRef, useCallback, useEffect } from 'react';
import { AuthenticatedTemplate, useMsal } from '@azure/msal-react';
import {
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    Avatar,
    Button,
    Text,
} from '@fluentui/react-components';
import {
    SignOutRegular,
    QuestionCircleRegular,
    DismissRegular,
    CopyRegular,
    CheckmarkRegular,
} from '@fluentui/react-icons';
import { useNavigationBarStyles } from './NavigationBar.styles';
import { clearAuthState, isTestMode } from '../../config';
import { useAppInsights } from '../../hooks/useAppInsights';
import JobLogo from '../../assets/job.svg';

export const NavigationBar: React.FC = () => {
    const { instance, accounts } = useMsal();
    const styles = useNavigationBarStyles();
    const telemetry = useAppInsights();

    const account = accounts[0];
    const userName = account?.name || account?.username || 'Manager';

    const [isSupportPanelOpen, setIsSupportPanelOpen] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);
    const supportButtonRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const closeSupportPanel = useCallback(() => {
        setIsSupportPanelOpen(false);
        requestAnimationFrame(() => {
            supportButtonRef.current?.focus();
        });
    }, []);

    useEffect(() => {
        if (isSupportPanelOpen) {
            requestAnimationFrame(() => {
                closeButtonRef.current?.focus();
            });
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    closeSupportPanel();
                }
            };
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isSupportPanelOpen, closeSupportPanel]);

    const handleCopyEmail = async () => {
        try {
            await navigator.clipboard.writeText('askhr@microsoft.com');
            setEmailCopied(true);
            setTimeout(() => setEmailCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy email:', err);
        }
    };

    const handleLogout = () => {
        clearAuthState();
        instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
        }).catch((error) => console.log(error));
    };

    const supportPanel = isSupportPanelOpen && (
        <>
            <div
                className={styles.panelOverlay}
                onClick={closeSupportPanel}
                aria-hidden="true"
            />
            <aside
                role="complementary"
                aria-label="Need Help?"
                className={`${styles.supportDrawer} ${styles.panelSurface}`}
            >
                <div className={styles.panelHeader}>
                    <h2 className={styles.panelTitle}>Need Help?</h2>
                    <Button
                        ref={closeButtonRef}
                        appearance="subtle"
                        aria-label="Close help panel"
                        icon={<DismissRegular />}
                        onClick={closeSupportPanel}
                    />
                </div>
                <div className={styles.supportPanelBody}>
                    <div className={styles.emailRow}>
                        <Text size={400}>
                            Email AskHR at{' '}
                            <a
                                href="mailto:askhr@microsoft.com?subject=Pre-Hire%20Activity%20Enquiry%20%E2%80%93%20Case%20Number%20%5BCase%20Number%5D&body=Hello%2C%0A%0AI%20am%20reaching%20out%20regarding%20my%20pre-hire%20activities.%0A%0AMy%20reference%20case%20number%20is%20%5BCase%20Number%5D%0A%0AI%20would%20appreciate%20your%20assistance%20with%20%5Bbriefly%20describe%20the%20issue%20you%20are%20facing%5D.%0A%0AThank%20you"
                                onClick={() => telemetry.trackEvent('SupportEmailClicked', { email: 'askhr@microsoft.com' })}
                            >
                                <strong>askhr@microsoft.com</strong>
                            </a>
                        </Text>
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={emailCopied ? <CheckmarkRegular /> : <CopyRegular />}
                            onClick={handleCopyEmail}
                            aria-label={emailCopied ? 'Copied!' : 'Copy email'}
                        />
                    </div>
                    <Text size={300} className={styles.supportPanelText}>
                        For a faster response, please include the candidate name &amp; HR case number in the subject line.
                    </Text>
                </div>
                <div className={styles.supportPanelFooter}>
                    <Button appearance="secondary" onClick={closeSupportPanel}>
                        Close
                    </Button>
                </div>
            </aside>
        </>
    );

    // In test mode, render without MSAL authentication wrapper
    if (isTestMode) {
        return (
            <header>
                <div className={styles.navbar}>
                    <div className={styles.brand}>
                        <img src={JobLogo} alt="" width="32" height="32" aria-hidden="true" />
                        <span className={styles.title}>
                            Onboarding Hub
                        </span>
                    </div>
                    <div className={styles.navActions}>
                        <button
                            ref={supportButtonRef}
                            className={styles.supportButton}
                            aria-label="Get support"
                            title="Get support"
                            onClick={() => setIsSupportPanelOpen(true)}
                        >
                            <QuestionCircleRegular fontSize={20} />
                        </button>
                        <Menu>
                            <MenuTrigger>
                                <Avatar
                                    className={styles.avatar}
                                    color="colorful"
                                    name="Test Manager"
                                    size={28}
                                    aria-label="User menu"
                                />
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem icon={<SignOutRegular />}>
                                        Sign Out
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                </div>
                {supportPanel}
            </header>
        );
    }

    return (
        <header>
            <div className={styles.navbar}>
                <div className={styles.brand}>
                    <img src={JobLogo} alt="" width="32" height="32" aria-hidden="true" />
                    <span className={styles.title}>
                        Onboarding Hub
                    </span>
                </div>
                <AuthenticatedTemplate>
                    <div className={styles.navActions}>
                        <button
                            ref={supportButtonRef}
                            className={styles.supportButton}
                            aria-label="Get support"
                            title="Get support"
                            onClick={() => setIsSupportPanelOpen(true)}
                        >
                            <QuestionCircleRegular fontSize={20} />
                        </button>
                        <Menu>
                            <MenuTrigger>
                                <Avatar
                                    className={styles.avatar}
                                    color="colorful"
                                    name={userName}
                                    size={28}
                                    aria-label="User menu"
                                />
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem icon={<SignOutRegular />} onClick={handleLogout}>
                                        Sign Out
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                </AuthenticatedTemplate>
            </div>
            {supportPanel}
        </header>
    );
};
